import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { parseUnifiCounts, unifiFreshness, unifiOnlineDevices } from '../lib/unifi';

export const OPEN_TICKET_STATUSES = ['open', 'in_progress', 'waiting_on_customer'] as const;

export interface PlatformMetrics {
  totalCustomers: number;
  activeCustomers: number;
  totalMrrCents: number;
  totalLocations: number;
  openTickets: number;
  /** Open tickets whose sla_deadline is already in the past. */
  slaBreaches: number;
  activeFieldTrips: number;
  deploymentsTotal: number;
  deploymentsByStatus: Record<string, number>;
  deploymentsBySolutionType: Record<string, number>;
  mrrBySlaTier: Record<string, { sum: number; count: number }>;
  auvik: {
    /** True only when Auvik has delivered rows (mapped tenants or devices). */
    connected: boolean;
    mappedTenants: number;
    devices: number;
    /** Open network alerts; null when Auvik is not connected. */
    openAlerts: number | null;
  };
  unifi: {
    sites: number;
    mappedSites: number;
    live: number;
    stale: number;
    offline: number;
    /** Sum of attributes.counts across sites; null when no site reports counts. */
    devicesOnline: number | null;
    devicesOffline: number | null;
    wifiClients: number | null;
    wiredClients: number | null;
    latestAt: string | null;
  };
}

export function usePlatformMetrics() {
  return useQuery({
    queryKey: ['admin-platform-metrics'],
    queryFn: async (): Promise<PlatformMetrics> => {
      const [customersRes, ticketsRes, tripsRes, deploymentsRes, locationsRes, tenantsRes, devicesRes, alertsRes, unifiRes] =
        await Promise.all([
          supabase.from('customers').select('id, mrr_cents, sla_tier, status').is('deleted_at', null),
          supabase.from('tickets').select('id, status, sla_deadline').in('status', [...OPEN_TICKET_STATUSES]),
          supabase.from('field_trips').select('id, status').in('status', ['scheduled', 'in_progress']),
          supabase.from('deployments').select('id, status, solution_type').neq('status', 'decommissioned'),
          supabase.from('locations').select('id', { count: 'exact', head: true }).is('deleted_at', null),
          supabase.from('auvik_tenant_map').select('id', { count: 'exact', head: true }).not('customer_id', 'is', null),
          supabase.from('devices').select('id', { count: 'exact', head: true }),
          supabase
            .from('network_alerts')
            .select('id', { count: 'exact', head: true })
            .is('resolved_at', null)
            .or('status.is.null,and(status.neq.cleared,status.neq.resolved)'),
          supabase.from('unifi_site_map').select('id, customer_id, updated_at, attributes'),
        ]);

      for (const res of [customersRes, ticketsRes, tripsRes, deploymentsRes, locationsRes, tenantsRes, devicesRes, alertsRes, unifiRes]) {
        if (res.error) throw res.error;
      }

      const customers = customersRes.data ?? [];
      const totalMrrCents = customers.reduce((sum, c) => sum + (c.mrr_cents ?? 0), 0);

      const mrrBySlaTier: Record<string, { sum: number; count: number }> = {};
      for (const c of customers) {
        const tier = c.sla_tier ?? 'standard';
        if (!mrrBySlaTier[tier]) mrrBySlaTier[tier] = { sum: 0, count: 0 };
        mrrBySlaTier[tier].sum += c.mrr_cents ?? 0;
        mrrBySlaTier[tier].count += 1;
      }

      const deploymentsByStatus: Record<string, number> = {};
      const deploymentsBySolutionType: Record<string, number> = {};
      for (const d of deploymentsRes.data ?? []) {
        const s = d.status ?? 'unknown';
        deploymentsByStatus[s] = (deploymentsByStatus[s] ?? 0) + 1;
        const st = d.solution_type ?? 'unknown';
        deploymentsBySolutionType[st] = (deploymentsBySolutionType[st] ?? 0) + 1;
      }

      const now = Date.now();
      const tickets = ticketsRes.data ?? [];
      const slaBreaches = tickets.filter(
        (t) => t.sla_deadline !== null && new Date(t.sla_deadline).getTime() < now,
      ).length;

      const mappedTenants = tenantsRes.count ?? 0;
      const devices = devicesRes.count ?? 0;
      const auvikConnected = mappedTenants > 0 || devices > 0;

      const unifiSites = unifiRes.data ?? [];
      let live = 0;
      let stale = 0;
      let offline = 0;
      let devicesOnline: number | null = null;
      let devicesOffline: number | null = null;
      let wifiClients: number | null = null;
      let wiredClients: number | null = null;
      let latestAt: string | null = null;
      for (const site of unifiSites) {
        const freshness = unifiFreshness(site.updated_at, now);
        if (freshness === 'live') live += 1;
        else if (freshness === 'stale') stale += 1;
        else offline += 1;
        if (!latestAt || site.updated_at > latestAt) latestAt = site.updated_at;
        const counts = parseUnifiCounts(site.attributes);
        const online = unifiOnlineDevices(counts);
        if (online !== null) devicesOnline = (devicesOnline ?? 0) + online;
        if (counts?.offlineDevice !== null && counts?.offlineDevice !== undefined) {
          devicesOffline = (devicesOffline ?? 0) + counts.offlineDevice;
        }
        if (counts?.wifiClient !== null && counts?.wifiClient !== undefined) {
          wifiClients = (wifiClients ?? 0) + counts.wifiClient;
        }
        if (counts?.wiredClient !== null && counts?.wiredClient !== undefined) {
          wiredClients = (wiredClients ?? 0) + counts.wiredClient;
        }
      }

      return {
        totalCustomers: customers.length,
        activeCustomers: customers.filter((c) => c.status === 'active').length,
        totalMrrCents,
        totalLocations: locationsRes.count ?? 0,
        openTickets: tickets.length,
        slaBreaches,
        activeFieldTrips: (tripsRes.data ?? []).length,
        deploymentsTotal: (deploymentsRes.data ?? []).length,
        deploymentsByStatus,
        deploymentsBySolutionType,
        mrrBySlaTier,
        auvik: {
          connected: auvikConnected,
          mappedTenants,
          devices,
          openAlerts: auvikConnected ? (alertsRes.count ?? 0) : null,
        },
        unifi: {
          sites: unifiSites.length,
          mappedSites: unifiSites.filter((s) => s.customer_id !== null).length,
          live,
          stale,
          offline,
          devicesOnline,
          devicesOffline,
          wifiClients,
          wiredClients,
          latestAt,
        },
      };
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}
